import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Mail, MessageSquare, CheckCircle, Clock, XCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const TICKET_STATUS_COLORS = {
  open: "text-yellow-400 border-yellow-600/30 bg-yellow-500/5",
  replied: "text-[#39FF14] border-[#39FF14]/30 bg-[#39FF14]/5",
  closed: "text-[#A1A1A1] border-[#1A1A1A] bg-[#0D0D0D]",
};

export default function AdminEmailsPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("tickets");
  const [tickets, setTickets] = useState([]);
  const [emailLog, setEmailLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [t, e] = await Promise.all([
        axios.get(`${API}/api/admin/contact-tickets`),
        axios.get(`${API}/api/admin/email-log`),
      ]);
      setTickets(t.data);
      setEmailLog(e.data);
    } catch (e) {
      console.error("Fetch failed", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== "admin") { navigate("/"); return; }
    fetchData();
  }, [user, authLoading, navigate, fetchData]);

  const handleUpdateTicket = async (ticketId, newStatus) => {
    setUpdating(ticketId);
    try {
      await axios.put(`${API}/api/admin/contact-tickets/${ticketId}`, { status: newStatus });
      fetchData();
    } catch (e) {
      console.error("Update failed", e);
    } finally {
      setUpdating(null);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#050505" }}>
        <div className="w-8 h-8 border border-[#39FF14] border-t-transparent animate-spin" />
      </div>
    );
  }

  const openTickets = tickets.filter(t => t.status === "open").length;

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#050505" }}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <p className="font-mono uppercase tracking-[0.3em] text-xs font-bold text-[#39FF14] mb-2">Admin</p>
            <h1 data-testid="admin-emails-heading" className="text-4xl sm:text-5xl font-black uppercase tracking-tighter text-[#F2F2F2]" style={{ fontFamily: "Chivo, sans-serif" }}>
              Messages & Emails
            </h1>
          </div>
          <Button onClick={() => navigate("/admin")} variant="outline" className="bg-transparent border-[#1A1A1A] hover:border-[#39FF14] text-[#A1A1A1] hover:text-[#39FF14] rounded-none font-mono text-xs uppercase tracking-wider" data-testid="back-to-admin-btn">
            <ArrowLeft className="h-4 w-4 mr-2" strokeWidth={1.5} />Products
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-[1px] bg-[#1A1A1A] mb-8">
          <div className="bg-[#0D0D0D] p-4 sm:p-6">
            <p className="font-mono uppercase tracking-[0.2em] text-[10px] font-bold text-[#A1A1A1] mb-1">Open Messages</p>
            <p className="font-mono text-2xl font-bold text-yellow-400">{openTickets}</p>
          </div>
          <div className="bg-[#0D0D0D] p-4 sm:p-6">
            <p className="font-mono uppercase tracking-[0.2em] text-[10px] font-bold text-[#A1A1A1] mb-1">Total Messages</p>
            <p className="font-mono text-2xl font-bold text-[#39FF14]">{tickets.length}</p>
          </div>
          <div className="bg-[#0D0D0D] p-4 sm:p-6 col-span-2 sm:col-span-1">
            <p className="font-mono uppercase tracking-[0.2em] text-[10px] font-bold text-[#A1A1A1] mb-1">Emails Sent</p>
            <p className="font-mono text-2xl font-bold text-[#39FF14]">{emailLog.length}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-[1px] mb-6">
          <button onClick={() => setTab("tickets")} className={`flex-1 py-3 font-mono text-xs uppercase tracking-[0.2em] font-bold transition-colors ${tab === "tickets" ? "bg-[#39FF14] text-[#050505]" : "bg-[#0D0D0D] text-[#A1A1A1] hover:text-[#39FF14] border border-[#1A1A1A]"}`}>
            <MessageSquare className="h-3 w-3 inline mr-2" />Customer Messages {openTickets > 0 && `(${openTickets} new)`}
          </button>
          <button onClick={() => setTab("emails")} className={`flex-1 py-3 font-mono text-xs uppercase tracking-[0.2em] font-bold transition-colors ${tab === "emails" ? "bg-[#39FF14] text-[#050505]" : "bg-[#0D0D0D] text-[#A1A1A1] hover:text-[#39FF14] border border-[#1A1A1A]"}`}>
            <Mail className="h-3 w-3 inline mr-2" />Email Log
          </button>
        </div>

        {/* Customer Messages */}
        {tab === "tickets" && (
          tickets.length === 0 ? (
            <div className="bg-[#0D0D0D] border border-[#1A1A1A] p-12 text-center">
              <MessageSquare className="h-12 w-12 text-[#1A1A1A] mx-auto mb-4" />
              <p className="text-[#A1A1A1]">No customer messages yet.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {tickets.map((ticket) => (
                <div key={ticket.id} data-testid={`ticket-${ticket.id}`} className="bg-[#0D0D0D] border border-[#1A1A1A] p-5">
                  <div className="flex flex-col sm:flex-row justify-between items-start gap-3 mb-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[#F2F2F2] text-sm font-bold">{ticket.name}</span>
                        <span className="text-[#A1A1A1] text-xs">&lt;{ticket.email}&gt;</span>
                      </div>
                      <p className="text-[#39FF14] text-xs font-mono uppercase tracking-wider">{ticket.subject}</p>
                      <p className="text-[#A1A1A1] text-[10px] font-mono mt-1">{new Date(ticket.created_at).toLocaleString()}</p>
                    </div>
                    <div className={`px-2 py-0.5 border text-[10px] font-mono uppercase tracking-wider ${TICKET_STATUS_COLORS[ticket.status] || ""}`}>
                      {ticket.status}
                    </div>
                  </div>
                  <div className="bg-[#050505] border border-[#1A1A1A] p-4 mb-3">
                    <p className="text-[#F2F2F2] text-sm whitespace-pre-wrap">{ticket.message}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <a href={`mailto:${ticket.email}?subject=Re: ${ticket.subject}`} className="inline-flex items-center gap-1 px-3 py-1.5 border border-[#39FF14]/50 text-[#39FF14] hover:bg-[#39FF14]/10 font-mono text-[10px] uppercase tracking-wider transition-colors" data-testid={`reply-${ticket.id}`}>
                      <Mail className="h-3 w-3" />Reply via Email
                    </a>
                    {ticket.status === "open" && (
                      <button onClick={() => handleUpdateTicket(ticket.id, "replied")} disabled={updating === ticket.id} className="px-3 py-1.5 border border-[#39FF14]/50 text-[#39FF14] hover:bg-[#39FF14]/10 font-mono text-[10px] uppercase tracking-wider disabled:opacity-50">
                        <CheckCircle className="h-3 w-3 inline mr-1" />Mark Replied
                      </button>
                    )}
                    {ticket.status !== "closed" && (
                      <button onClick={() => handleUpdateTicket(ticket.id, "closed")} disabled={updating === ticket.id} className="px-3 py-1.5 border border-[#1A1A1A] text-[#A1A1A1] hover:text-red-400 hover:border-red-500/50 font-mono text-[10px] uppercase tracking-wider disabled:opacity-50">
                        <XCircle className="h-3 w-3 inline mr-1" />Close
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )
        )}

        {/* Email Log */}
        {tab === "emails" && (
          emailLog.length === 0 ? (
            <div className="bg-[#0D0D0D] border border-[#1A1A1A] p-12 text-center">
              <Mail className="h-12 w-12 text-[#1A1A1A] mx-auto mb-4" />
              <p className="text-[#A1A1A1]">No emails sent yet.</p>
            </div>
          ) : (
            <div className="border border-[#1A1A1A]">
              <div className="hidden sm:grid grid-cols-[1fr_1fr_80px_160px] gap-4 bg-[#0D0D0D] p-3 border-b border-[#1A1A1A]">
                <span className="font-mono uppercase tracking-[0.2em] text-[10px] font-bold text-[#39FF14]">To</span>
                <span className="font-mono uppercase tracking-[0.2em] text-[10px] font-bold text-[#39FF14]">Subject</span>
                <span className="font-mono uppercase tracking-[0.2em] text-[10px] font-bold text-[#39FF14]">Status</span>
                <span className="font-mono uppercase tracking-[0.2em] text-[10px] font-bold text-[#39FF14]">Date</span>
              </div>
              <div className="max-h-[600px] overflow-y-auto">
                {emailLog.map((email) => (
                  <div key={email.id} className="flex flex-col sm:grid sm:grid-cols-[1fr_1fr_80px_160px] gap-2 sm:gap-4 p-3 bg-[#0D0D0D] border-b border-[#1A1A1A]/50 hover:bg-[#141414] transition-colors">
                    <span className="text-[#F2F2F2] text-sm truncate">{email.to}</span>
                    <span className="text-[#A1A1A1] text-sm truncate">{email.subject}</span>
                    <span className={`font-mono text-xs ${email.status === "sent" ? "text-[#39FF14]" : "text-red-400"}`}>{email.status}</span>
                    <span className="font-mono text-xs text-[#A1A1A1]">{new Date(email.created_at).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          )
        )}
      </div>
    </div>
  );
}
